import React, { useState } from 'react';
import { db } from '../../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import './RegisterAsset.css';
import { useTranslation } from 'react-i18next';

const RegisterAsset = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    serial_number: '',
    model: '',
    category: '',
    location: '',
    admin_pin: '',
    guide_use: '',
    safety_conditions: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [assetLink, setAssetLink] = useState('');
  const navigate = useNavigate();
  const { user } = useUser();

  const generateToken = () => {
    const chars = 'abcdef0123456789';
    let token = '';
    for (let i = 0; i < 32; i++) {
      token += chars[Math.floor(Math.random() * chars.length)];
    }
    return token;
  };

  const validate = () => {
    const newErrors = {};
    if (!form.serial_number.trim()) newErrors.serial_number = t('registerAsset.errors.serialNumber');
    if (!form.model.trim()) newErrors.model = t('registerAsset.errors.model');
    if (!form.category.trim()) newErrors.category = t('registerAsset.errors.category');
    if (!form.location.trim()) newErrors.location = t('registerAsset.errors.location');
    if (!form.admin_pin.trim()) newErrors.admin_pin = t('registerAsset.errors.adminPin');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.coffee_shop_id) return alert(t('registerAsset.errors.missingContext'));
    if (!validate()) return;

    setLoading(true);
    const token = generateToken();
    const fullLink = `https://loyaltap.flexpayz.com/asset/${token}`;

    try {
      await addDoc(collection(db, 'assets'), {
        ...form,
        token,
        coffee_shop_id: user.coffee_shop_id,
        created_by: user.email,
        createdAt: serverTimestamp(),
      });
      setAssetLink(fullLink);
      setForm({ serial_number: '', model: '', category: '', location: '', admin_pin: '', guide_use: '', safety_conditions: '' });
      setErrors({});
    } catch (err) {
      console.error('Error registering asset:', err);
      alert(t('registerAsset.errors.registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    const canvas = document.getElementById("qrCodeCanvas");
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `asset_qr_${form.serial_number}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <div className="register-asset-container animate-fadeIn">
      <h2>{t('registerAsset.title')}</h2>

      <form onSubmit={handleSubmit} className="register-asset-form">
        <div>
          <input
            type="text"
            placeholder={t('registerAsset.serialNumber')}
            value={form.serial_number}
            onChange={e => setForm({ ...form, serial_number: e.target.value })}
          />
          {errors.serial_number && <p className="text-red-600 text-sm">{errors.serial_number}</p>}
        </div>

        <div>
          <input
            type="text"
            placeholder={t('registerAsset.model')}
            value={form.model}
            onChange={e => setForm({ ...form, model: e.target.value })}
          />
          {errors.model && <p className="text-red-600 text-sm">{errors.model}</p>}
        </div>

        <div>
          <input
            type="text"
            placeholder={t('registerAsset.category')}
            value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })}
          />
          {errors.category && <p className="text-red-600 text-sm">{errors.category}</p>}
        </div>

        <div>
          <input
            type="text"
            placeholder={t('registerAsset.location')}
            value={form.location}
            onChange={e => setForm({ ...form, location: e.target.value })}
          />
          {errors.location && <p className="text-red-600 text-sm">{errors.location}</p>}
        </div>

        <div>
          <input
            type="password"
            placeholder={t('registerAsset.adminPin')}
            value={form.admin_pin}
            onChange={e => setForm({ ...form, admin_pin: e.target.value })}
          />
          {errors.admin_pin && <p className="text-red-600 text-sm">{errors.admin_pin}</p>}
        </div>

        <div>
          <textarea
            placeholder={t('registerAsset.guideUse')}
            value={form.guide_use}
            onChange={e => setForm({ ...form, guide_use: e.target.value })}
          />
        </div>

        <div>
          <textarea
            placeholder={t('registerAsset.safetyConditions')}
            value={form.safety_conditions}
            onChange={e => setForm({ ...form, safety_conditions: e.target.value })}
          />
        </div>

        <button type="submit" disabled={loading}>
          {loading ? t('registerAsset.loading') : t('registerAsset.submit')}
        </button>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-gray-200 hover:bg-gray-300 text-black font-medium py-2 px-4 rounded mb-4"
        >
          ← {t('registerAsset.backToDashboard')}
        </button>
      </form>

      {assetLink && (
        <div className="asset-registered-box animate-fadeIn">
          <h3>{t('registerAsset.assetRegistered')}</h3>
          <p>{assetLink}</p>
          <QRCodeCanvas id="qrCodeCanvas" value={assetLink} size={180} level="H" />
          <div className="mt-4">
            <button onClick={downloadQR}>{t('registerAsset.downloadQR')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterAsset;
